import { cloneDeep } from "lodash";
import Cookies from "universal-cookie";
import { clearUserAndStationState } from "./Store";

const cookies = new Cookies();

const Actions = {
  SetVars: "SetVars",
  SetVarsError: "SetVarsError",
  SetUser: "SetUser",
  SetUserName: "SetUserName",
  SetBoxEvents: "SetBoxEvents",
  SetBoxSettings: "SetBoxSettings",
  UpdateBoxEvent: "UpdateBoxEvent",
  UpdateBoxSubEvent: "UpdateBoxSubEvent",
  UpdateBoxSettings: "UpdateBoxSettings",
  UpdateCurrentPressure: "UpdateCurrentPressure",
  UpdateCurrentVitals: "UpdateCurrentVitals",
  SetCurrentPressure: "SetCurrentPressure",
  SetCurrentVitals: "SetCurrentVitals",
  SetCareUnit: "SetCareUnit",
  SetCareUnits: "SetCareUnits",
  SetControlBoxes: "SetControlBoxes",
  Logout: "Logout",
  SetCancelSubscriptions: "SetCancelSubscriptions",
  SetDashboardView: "SetDashboardView",
  SetPatientTransfer: "SetPatientTransfer",
  SetAccounts: "SetAccounts",
  SetAccount: "SetAccount",
  SetAccountDetails: "SetAccountDetails", // {'account': 'the account', 'role': 'the role from cognito'}
  SetAccountDetailsAccount: "SetAccountDetailsAccount",
  SetAccountDetailsRole: "SetAccountDetailsRole",
  CreateOrUpdateAccount: "CreateOrUpdateAccount",
  SetRole: "SetRole",
};

// Add Actions here that simply map to setting a value in the store
// The value of entry is the key to set in the store.
const setValueActions = {
  [Actions.SetVars]: "vars",
  [Actions.SetBoxEvents]: "boxEvents",
  [Actions.SetCurrentPressure]: "currentPressure",
  [Actions.SetCurrentVitals]: "currentVitals",
  [Actions.SetVarsError]: "varsError",
  [Actions.SetCancelSubscriptions]: "cancelSubscriptions",
  [Actions.SetDashboardView]: "dashboardView",
  [Actions.SetPatientTransfer]: "patientTransfer",
  [Actions.SetAccounts]: "accounts",
  [Actions.SetAccountDetails]: "accountDetails",
  [Actions.SetCareUnits]: "scStations",
  [Actions.SetRole]: "role",
  [Actions.SetUserName]: "userName",
  [Actions.SetNeedToPlayAlert]: "needToPlayAlert",
  [Actions.SetNeedToPlayReposition]: "needToPlayReposition",
  [Actions.SetRepositionPlaying]: "repositionPlaying",
};

const Reducer = (state, action) => {
  if (Actions[action.type]) {
    // Process simple set-value Actions
    if (action.type in setValueActions) {
      return {
        ...state,
        [setValueActions[action.type]]: action.payload,
      };
    }
    if (action.type === Actions.SetUser) {
      const user = action.payload;
      const sessionInfo = {};
      return {
        ...state,
        user,
        ...sessionInfo,
      };
    }

    if (action.type === Actions.UpdateBoxEvent) {
      // action.payload is an event
      // update the event in the store for which there is a new event
      let boxEvents;
      const index = state.boxEvents.findIndex(
        (event) => event.boxId === action.payload.boxId
      );
      if (index === -1) {
        boxEvents = state.boxEvents.concat(action.payload);
      } else {
        boxEvents = [...state.boxEvents];
        const boxEvent = action.payload;
        boxEvent.subEvent = state.boxEvents[index].subEvent;
        boxEvents[index] = boxEvent;
      }
      return {
        ...state,
        boxEvents,
      };
    }

    if (action.type === Actions.UpdateBoxSubEvent) {
      // action.payload is an event
      // update the subevent for the main event for matching boxid
      let boxEvents = [...state.boxEvents];
      const index = state.boxEvents.findIndex(
        (event) => event.boxId === action.payload.boxId
      );
      if (index !== -1) {
        const boxEvent = state.boxEvents[index];
        const previous = boxEvent.subEvent;
        let next;
        if (previous) {
          next = cloneDeep(previous);
          if (action.payload.PatientState) {
            next.PatientState = action.payload.PatientState;
          }
          if (action.payload.PadConnectionStatus) {
            next.PadConnectionStatus = action.payload.PadConnectionStatus;
          }
          if (action.payload.BoxTransmissionStatus) {
            next.BoxTransmissionStatus = action.payload.BoxTransmissionStatus;
          }
          if (action.payload.RepositionLevel) {
            next.RepositionLevel = action.payload.RepositionLevel;
          }
          boxEvent.subEvent = next;
        } else {
          next = action.payload;
        }
        boxEvents[index].subEvent = next;
      } else {
        console.error(
          `Missed index for subEvent! Payload: ${JSON.stringify(
            action.payload
          )}`
        );
      }

      return {
        ...state,
        boxEvents,
      };
    }

    if (action.type === Actions.UpdateCurrentPressure) {
      // action.payload is an pressure level data for a single box
      let currentPressure;
      const index = state.currentPressure.findIndex(
        (pressureData) => pressureData.boxId === action.payload.boxId
      );
      if (index === -1) {
        currentPressure = state.currentPressure.concat(action.payload);
      } else {
        currentPressure = [...state.currentPressure];
        currentPressure[index] = action.payload;
      }
      return {
        ...state,
        currentPressure,
      };
    }

    if (action.type === Actions.UpdateCurrentVitals) {
      // action.payload is vitals data for a single box
      let currentVitals;
      const index = state.currentVitals.findIndex(
        (vitalsData) => vitalsData.boxId === action.payload.boxId
      );
      if (index === -1) {
        currentVitals = state.currentVitals.concat(action.payload);
      } else {
        currentVitals = [...state.currentVitals];
        currentVitals[index] = action.payload;
      }
      return {
        ...state,
        currentVitals,
      };
    }

    if (action.type === Actions.SetControlBoxes) {
      if (!action.payload) action.payload = [];
      const patientSnToBox = {};
      action.payload.forEach((box) => {
        if (box.patientSn) {
          patientSnToBox[box.patientSn] = box;
        }
      });
      return {
        ...state,
        controlBoxes: action.payload,
        patientSnToBox: { ...state.patientSnToBox, ...patientSnToBox },
      };
    }
    if (action.type === Actions.SetBoxSettings) {
      if (!action.payload) action.payload = [];
      // transform settings into map also
      const controlBoxIdToSettings = {};
      action.payload.forEach((boxSetting) => {
        controlBoxIdToSettings[boxSetting.boxId] = boxSetting;
      });
      return {
        ...state,
        boxSettings: action.payload,
        controlBoxIdToSettings,
      };
    }
    if (action.type === Actions.UpdateBoxSettings) {
      // the reducer inserts or updates settings for one or more control boxes
      // store.boxSettings is a map of boxId => settings
      // action.payload should be an array of settings for each box,
      //   as for example received in an appsync subscription or

      let settingsForBoxesToBeAdddedOrUpdated;
      if (action.payload.hasOwnProperty("boxId")) {
        settingsForBoxesToBeAdddedOrUpdated = [action.payload];
      } else {
        settingsForBoxesToBeAdddedOrUpdated = action.payload;
      }

      let boxSettings = [...state.boxSettings];
      for (let settingsForBox of settingsForBoxesToBeAdddedOrUpdated) {
        const index = boxSettings.findIndex(
          (box) => box.boxId === settingsForBox.boxId
        );
        if (index === -1) {
          // since we already did the object deference above
          // we can just use push here instead of concat, which would
          // typically be required to ensure that updates to the store
          // are propagated to components
          boxSettings.push(settingsForBox);
        } else {
          if (settingsForBox.hasOwnProperty("generationSettings")) {
            boxSettings[index].generationSettings =
              settingsForBox.generationSettings;
          }
          // later there will be other lines here for networkSettings and iotSettings
        }
      }

      return {
        ...state,
        boxSettings,
      };
    }

    if (action.type === Actions.SetCareUnit) {
      // the place to write this to a cookie
      const scStation = action.payload;
      cookies.set("scStation", scStation, {
        path: "/",
        sameSite: "strict",
      });
      // TODO: we should clear the boxSettings and currentPressure upon switching Care Units
      // return to this later when we have tests with multiple care units
      return {
        ...state,
        // ...clearStationState,
        scStation: scStation,
      };
    }
    if (action.type === Actions.SetCareUnitAlerts) {
      action.payload.onActionComplete();
      return {
        ...state,
        // ...clearStationState,
        scStation: action.payload.station,
      };
    }
    if (action.type === Actions.Logout) {
      cookies.remove("scStation");
      sessionStorage.clear();

      return {
        ...state,
        ...clearUserAndStationState,
        cancelSubscriptions: true,
      };
    }
    if (action.type === Actions.SetAccount) {
      // must return a new array to cause the state update
      let accounts;
      if (action.payload) {
        const index = state.accounts.findIndex(
          (account) => account.id === action.payload.id
        );
        if (index === -1) {
          accounts = state.accounts.concat(action.payload);
        } else {
          accounts = [...state.accounts];
          accounts[index] = action.payload;
        }
      }
      return {
        ...state,
        accounts,
      };
    }
    throw new Error(`action type not handled: ${action.type}`);
  } else {
    console.log(`unknown action type: ${action.type}`);
  }
  // Other custom Action logic can go here.
  return state;
};
export { Actions };
export default Reducer;
